import { SvgIconComponent } from '@mui/icons-material';
import React, { useMemo } from 'react';
import TooltipHardened from '../TooltipHardened';
import { Box, BoxProps, Button, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { CenteredColumn } from '../Stacks';

export interface InputActionProps {
  icon: SvgIconComponent;
  onClick: () => void;
  disabled?: boolean;
  /** makes the icon move */
  processing?: boolean;
  tooltip?: React.ReactNode;
  /** for setting correct border radius. default: right */
  side?: 'left' | 'right';
}

/** A styled icon button, made to be attached to StyledInputs */
export const InputAction = ({
  side,
  onClick,
  processing,
  tooltip,
  icon,
  disabled,
}: InputActionProps): React.JSX.Element => {
  const ActionIcon = useMemo(() => icon, [icon]);

  const radiusProps = useMemo(() => {
    switch (side) {
      case 'left':
        return {
          borderRight: 0,
          borderTopLeftRadius: 4,
          borderBottomLeftRadius: 4,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
        };
      default:
        return {
          borderLeft: 0,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderTopRightRadius: 4,
          borderBottomRightRadius: 4,
        };
    }
  }, [side]);

  return (
    <IconButton
      onClick={onClick}
      disabled={disabled}
      sx={{
        border: 1,
        borderColor: 'action.disabled',
        ...radiusProps,
        '&:hover': {
          borderColor: 'text.primary',
        },
      }}
    >
      <TooltipHardened tooltip={tooltip}>
        <ActionIcon
          sx={{
            animation: !!processing ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      </TooltipHardened>
    </IconButton>
  );
};

export interface InputCollapsibleProps {
  content: React.ReactNode;
  open: boolean;
}

/** A styled container intended to be attached to StyledInputs */
export const InputCollapsible = ({ open, content }: InputCollapsibleProps): React.JSX.Element => (
  <Collapse in={open}>
    <Box
      sx={{
        p: 2,
        border: 1,
        borderTop: 0,
        borderRadius: 1,
        borderColor: 'action.disabled',
      }}
    >
      {content}
    </Box>
  </Collapse>
);

export interface InputAdditionsProps {
  action?: InputActionProps;
  collapsible?: InputCollapsibleProps;
  /** should be a styled input element */
  children: React.JSX.Element;
}

export const InputAdditions = ({ action, collapsible, children }: InputAdditionsProps): React.JSX.Element => {
  const actionWrap = useMemo(() => {
    if (action === undefined) {
      return children;
    }

    const actionSide = action.side ?? 'right';
    return (
      <Stack direction={'row'} spacing={0}>
        {actionSide === 'left' && <InputAction {...action} />}
        <Box
          width={'100%'}
          sx={{
            '& .MuiOutlinedInput-notchedOutline':
              actionSide === 'left'
                ? {
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }
                : {
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  },
          }}
        >
          {children}
        </Box>
        {actionSide === 'right' && <InputAction {...action} />}
      </Stack>
    );
  }, [action, children]);

  const collapseWrap = useMemo(() => {
    if (collapsible === undefined) {
      return actionWrap;
    }

    return (
      <CenteredColumn width={'100%'} spacing={0}>
        {actionWrap}
        <InputCollapsible {...collapsible} />
      </CenteredColumn>
    );
  }, [actionWrap, collapsible]);

  return collapseWrap;
};

export interface InputPresetOption {
  label?: React.ReactNode;
  value: string | number;
  textColor?: BoxProps['color'];
  backgroundColor?: BoxProps['bgcolor'];
  tooltip?: React.ReactNode;
}
interface InputPresetsProps {
  children: React.JSX.Element;
  selected?: string | number;
  onClick: (value: string | number) => void;
  options: InputPresetOption[];
}

export const InputPresets = ({ options, selected, onClick, children }: InputPresetsProps): React.JSX.Element => {
  const clickables = useMemo(
    () => (
      <Stack direction={'row'} spacing={1} width={'100%'}>
        {options.map((o, i) => (
          <TooltipHardened key={i} tooltip={o.tooltip ?? (o.label !== o.value ? o.value : undefined)}>
            <Button
              variant={selected === o.value ? 'contained' : 'outlined'}
              onClick={() => {
                onClick(o.value);
              }}
              sx={{
                backgroundColor: o.backgroundColor,
                boxShadow: selected === o.value ? 4 : undefined,
                width: '100%',
                minWidth: 0,
                minHeight: 30,
                '&: hover': {
                  backgroundColor: o.backgroundColor,
                },
              }}
            >
              <Typography fontSize={'0.75rem'} color={o.textColor}>
                {o.label ?? ''}
              </Typography>
            </Button>
          </TooltipHardened>
        ))}
      </Stack>
    ),
    [options, onClick, selected],
  );

  return (
    <CenteredColumn width={'100%'} spacing={1}>
      {children}
      {clickables}
    </CenteredColumn>
  );
};
